import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { buildCompareSections, bestValueForRow } from "@/lib/compareCars";
import { proxied } from "@/lib/imageProxy";

export const CompareModal = ({ open, onClose, cars }) => {
  const list = cars?.length >= 2 ? cars : null;
  const sections = useMemo(() => (list ? buildCompareSections(list) : []), [list]);

  if (!list) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="compare-modal"
        className="max-w-5xl bg-anthracite-800 border border-white/10 text-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">Autók összehasonlítása</DialogTitle>
          <p className="text-xs text-slate-500 font-normal mt-1">
            Műszaki adatok és felszereltség — a külső megjelenés a képeken látszik.
          </p>
        </DialogHeader>

        <div className="overflow-x-auto">
          <table data-testid="compare-table" className="w-full mt-4 text-sm">
            <thead>
              <tr>
                <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-normal p-3 w-44 sticky left-0 bg-anthracite-800 z-10" />
                {list.map((c) => (
                  <th key={c.id} className="p-3 min-w-[168px] align-top">
                    <div className="aspect-[16/10] rounded-xl overflow-hidden mb-2">
                      <img
                        src={proxied(c.image)}
                        alt={c.model}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div className="text-xs text-slate-400">{c.make}</div>
                    <div className="font-heading text-base font-medium leading-snug">{c.model}</div>
                    {c.listing_title && (
                      <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{c.listing_title}</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <React.Fragment key={section.title}>
                  <tr className="border-t border-white/10">
                    <td
                      colSpan={list.length + 1}
                      className="p-3 pt-5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-electric/90"
                    >
                      {section.title}
                    </td>
                  </tr>
                  {section.rows.map((row) => {
                    const best = bestValueForRow(row, list);
                    return (
                      <tr key={`${section.title}-${row.label}`} className="border-t border-white/5">
                        <td className="p-3 text-xs text-slate-400 sticky left-0 bg-anthracite-800/95 z-[1]">
                          {row.label}
                        </td>
                        {list.map((c) => {
                          const raw = row.get(c);
                          const display = row.fmt ? row.fmt(raw) : raw;
                          const isBest =
                            !row.isFeature &&
                            best !== null &&
                            typeof raw === "number" &&
                            raw === best;
                          return (
                            <td
                              key={c.id}
                              className={`p-3 text-center ${
                                row.isFeature
                                  ? raw
                                    ? "text-emerald-400 font-medium"
                                    : "text-slate-600"
                                  : isBest
                                    ? "text-electric-light font-semibold"
                                    : "text-slate-200"
                              }`}
                            >
                              {display}
                              {isBest && <span className="ml-1 text-[10px] align-top">★</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-2">★ = számoknál a legjobb érték · ✓ = van a listán</p>
      </DialogContent>
    </Dialog>
  );
};
